import React, { useEffect, useState } from 'react';
import '../styles/Auth.css';
import { Button, Input, OrDivider } from '../components';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { checkOnAuthStateChanged, completeSignUp, getDocument, getUser, recordEvent, resetPassword, signInWithGoogle } from '../functions';
import { GoogleLogo } from '../assets/icons';
import logo from '../logo.svg';
import Loading from '../components/Loading';
import { ONBOARDING_PATHS } from './onboardingRoutes';
import { setPendingRedirect, consumePendingRedirect, clearPendingRedirect } from '../utils/pendingRedirect';
import { peekPendingConnectAi, clearPendingConnectAi } from '../utils/pendingConnectAi';

function Login() {
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [disabled, setDisabled] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [loading, setLoading] = useState(false);
	// Read synchronously so layout is correct on first render with no flash
	const [lastAuthMethod] = useState(() => localStorage.getItem('lastAuthMethod'));
	const [showEmailForm, setShowEmailForm] = useState(false);
	const [showForgotPassword, setShowForgotPassword] = useState(false);
	const [resetEmail, setResetEmail] = useState("");
	const [resetSent, setResetSent] = useState(false);
	const [resetLoading, setResetLoading] = useState(false);
	const location = useLocation();
	const state = location.state;
	const navigate = useNavigate();
	const [searchParams] = useSearchParams({});
	const emailParam = searchParams.get("email");

	const emailFirst = lastAuthMethod === "email";

	useEffect(() => {
		document.title = "Login | Clozers";

		// Honour ?email= prefill (used by /claim's email-collision routing).
		if (emailParam) setEmail(emailParam);

		// Persist any incoming deep-link target durably so it survives a full
		// reload and the Login<->SignUp footer hop. Covers every per-page
		// navigate('/login', { state:{ path }}) redirect. Invite/claim paths
		// are silently refused by the validator (their tokens stay authoritative).
		if (state?.path) setPendingRedirect(state.path);

		const fetchUserData = async() => {
			const user = await checkOnAuthStateChanged();
			if (user.status === 200) {
				// If they were sent to /login by /claim (pendingClaimToken
				// in localStorage) and they're already signed in, route
				// straight to /claim to consume — don't bounce to /.
				const pendingClaim = localStorage.getItem('pendingClaimToken');
				if (pendingClaim) {
					localStorage.removeItem('pendingClaimToken');
					navigate(`/claim?token=${pendingClaim}`, { replace: true });
					return;
				}
				navigate("/");
			}
		};

		fetchUserData();
	}, [navigate, emailParam, state]);

	const googleSignIn = async() => {
		setDisabled(true);
		setLoading(true);
		const loginUser = await signInWithGoogle();
		if (loginUser.status === 200) {
			const colRef = "users";
			const userId = loginUser.user.uid;
			const checkUserDocument = await getDocument(colRef, userId);
			if (checkUserDocument.status === 200) {
				complete(loginUser, 'google');
			} else {
				await completeSignUp(loginUser, true, email, "", "");
				const pendingClaim = localStorage.getItem('pendingClaimToken');
				const pendingToken = localStorage.getItem('pendingInviteToken');
				const pendingConnectAi = peekPendingConnectAi();
				// Brand-new Google account → must go through onboarding (it has a
				// fresh, empty workspace), never a company-scoped deep link.
				// Discard any preserved redirect; new users always land at '/'.
				clearPendingRedirect();
				if (pendingClaim) {
					localStorage.removeItem('pendingClaimToken');
					navigate(`/claim?token=${pendingClaim}`);
				} else if (pendingConnectAi) {
					// Claude / MCP connector signup via the Login page's Google
					// button → return to the consent screen to Authorize.
					clearPendingConnectAi();
					recordEvent("connect_ai_signed_up", { method: 'google', mode: 'create' });
					navigate(`/connect-ai?${pendingConnectAi}`);
				} else if (pendingToken) {
					localStorage.removeItem('pendingInviteToken');
					navigate(`/accept-invite?token=${pendingToken}`);
				} else {
					navigate(ONBOARDING_PATHS.business, { replace: true, state: state });
				}
			}
		} else {
			const code = loginUser.code;
			if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
				setDisabled(false);
				setLoading(false);
				return;
			} else if (code === "auth/network-request-failed") {
				setErrorMessage("Connection failed — please try again");
			} else {
				setErrorMessage("Google sign-in failed — please try again");
			}
			setDisabled(false);
			setLoading(false);
		}
	};

	const signInWithEmail = async() => {
		setLoading(true);
		setDisabled(true);
		const loginUser = await getUser(email, password);

		if (loginUser.status === 200) {
			complete(loginUser);
		} else {
			const code = loginUser.code;
			if (code === "auth/wrong-password" || code === "auth/user-not-found" || code === "auth/invalid-credential") {
				setErrorMessage("Incorrect email or password");
			} else if (code === "auth/invalid-email") {
				setErrorMessage("Please enter a valid email address");
			} else if (code === "auth/network-request-failed") {
				setErrorMessage("Connection failed — please try again");
			} else if (code === "auth/too-many-requests") {
				setErrorMessage("Too many attempts — please try again later");
			} else {
				setErrorMessage("Something went wrong — please try again");
			}
			setDisabled(false);
			setLoading(false);
		}
	};

	const complete = async(loginUser, method = 'email') => {
		const colRef = "users";
		const userId = loginUser.user.uid;
		const queryUserDoc = await getDocument(colRef, userId);

		if (queryUserDoc.status === 200) {
			recordEvent("Login", { userId: userId });
			setErrorMessage("");

			// Pay-first email-collision path: paying email already had a
			// free account. /claim parked the token and sent them here; once
			// they prove ownership (sign-in), attach the subscription before
			// resuming wherever they were headed.
			const pendingClaim = localStorage.getItem('pendingClaimToken');
			const pendingToken = localStorage.getItem('pendingInviteToken');
			const pendingConnectAi = peekPendingConnectAi();
			if (pendingClaim) {
				localStorage.removeItem('pendingClaimToken');
				navigate(`/claim?token=${pendingClaim}`);
			} else if (pendingConnectAi) {
				// Returning user authorizing the Claude / MCP connector — back to consent.
				clearPendingConnectAi();
				recordEvent("connect_ai_signed_up", { method, mode: 'signin' });
				navigate(`/connect-ai?${pendingConnectAi}`);
			} else {
				// Returning user. Precedence: deep-link redirect > invite > home.
				// The breadcrumb is the durable source of truth for deep links
				// (seeded from state.path on mount); the validator refuses
				// /accept-invite, so invites stay on the pendingInviteToken path.
				const redirect = consumePendingRedirect();
				if (redirect) {
					navigate(redirect);
				} else if (pendingToken) {
					localStorage.removeItem('pendingInviteToken');
					navigate(`/accept-invite?token=${pendingToken}`);
				} else {
					navigate("/");
				}
			}
			setLoading(false);
			setDisabled(false);
		} else {
			setErrorMessage("Something went wrong — please try again");
			setDisabled(false);
			setLoading(false);
		}
	};

	const handleForgotPassword = () => {
		setResetEmail(email);
		setShowForgotPassword(true);
		setResetSent(false);
		setErrorMessage("");
	};

	const sendResetEmail = async() => {
		if (!resetEmail) return;
		setErrorMessage("");
		setResetLoading(true);
		const result = await resetPassword(resetEmail, window.location.origin + '/login');
		setResetLoading(false);
		if (result.status === 200) {
			setResetSent(true);
		} else {
			const code = result.code;
			if (code === "auth/invalid-email") {
				setErrorMessage("Please enter a valid email address");
			} else if (result.status === 406 || code === "auth/network-request-failed") {
				setErrorMessage("Connection failed — please try again");
			} else {
				setErrorMessage("Something went wrong — please try again");
			}
		}
	};

	const loginButtonDisabled = email === "" || password === "" || disabled;

	const emailForm = (
		<div className="auth-form">
			<Input
				label={"Email"}
				labelType={"inside"}
				secondaryLabelGrey={null}
				info={false}
				value={email}
				onChange={(e) => setEmail(e.target.value)}
				placeholder={"youremail@email.com"}
				type="email"
				leftUnit={null}
				rightUnit={null}
				unitSwitch={false}
				leftUnitSelected={true}
				unitSwitchFunc={null}
				disabled={loading}
				multiline={false}
				error={false}
				onMouseEnter={null}
				onMouseLeave={null}
				reset={false}
				resetFunc={null}
				resetPosition={null}
				focusMultiline={false}
				prompt={false}
			/>
			<Input
				label={"Password"}
				labelType={"inside"}
				secondaryLabelGrey={null}
				info={false}
				value={password}
				onChange={(e) => setPassword(e.target.value)}
				placeholder={"••••••••••"}
				type="password"
				leftUnit={null}
				rightUnit={null}
				unitSwitch={false}
				leftUnitSelected={true}
				unitSwitchFunc={null}
				disabled={loading}
				multiline={false}
				error={false}
				onMouseEnter={null}
				onMouseLeave={null}
				reset={false}
				resetFunc={null}
				resetPosition={null}
				focusMultiline={false}
				prompt={false}
			/>
			<Button
				buttonType="primary-button"
				size="large"
				text={loading ? "Logging in…" : "Login"}
				cta={signInWithEmail}
				disabled={loginButtonDisabled}
				leftIcon={null}
				leftIconClass=""
				rightIcon={null}
				rightIconClass=""
				alt=""
				standardIcon={false}
				badge={false}
				badgeVal={null}
				badgeBG={null}
				buttonRef={null}
				spinner={loading}
				goldAccent={emailFirst}
			/>
			{emailFirst && (
				<span className="label-regular colour-secondary auth-method-hint">
					You used this last time
				</span>
			)}
		</div>
	);

	const googleButton = (
		<div className="auth-google-section">
			<Button
				buttonType="third-party-button"
				size="large"
				text="Continue with Google"
				cta={googleSignIn}
				disabled={disabled}
				leftIcon={<GoogleLogo />}
				leftIconClass=""
				rightIcon={null}
				rightIconClass=""
				alt=""
				standardIcon={false}
				badge={false}
				badgeVal={null}
				badgeBG={null}
				buttonRef={null}
				spinner={loading}
				goldAccent={lastAuthMethod === "google"}
			/>
			{lastAuthMethod === "google" && (
				<span className="label-regular colour-secondary auth-method-hint">
					You used this last time
				</span>
			)}
		</div>
	);

	return (
		<div className="auth-outer-container">
			{loading && (
				<div className="auth-loading-overlay">
					<Loading />
				</div>
			)}
			<div className="auth-inner-container">

				<div className="auth-logo-container">
					<img src={logo} alt="Clozers" className="auth-logo" />
				</div>

				<div className="auth-header-container">
					<h1 className="heading-large-bold colour-primary">
						Welcome back
					</h1>
				</div>

				{emailFirst ? emailForm : googleButton}

				<OrDivider />

				{emailFirst ? (
					<span
						className="body-regular-underline colour-link cursor-pointer"
						onClick={googleSignIn}
					>
						Continue with Google
					</span>
				) : showEmailForm ? (
					emailForm
				) : (
					<span
						className="body-regular-underline colour-link cursor-pointer"
						onClick={() => { setShowEmailForm(true); setErrorMessage(""); }}
					>
						Sign in with email
					</span>
				)}

				{(emailFirst || showEmailForm) && !showForgotPassword && !resetSent && (
					<span
						className="label-regular colour-link auth-forgot-link"
						onClick={handleForgotPassword}
					>
						Forgot your password?
					</span>
				)}

				{showForgotPassword && !resetSent && (
					<div className="auth-reset-panel">
						<div className="auth-reset-header">
							<span className="body-semibold colour-primary">
								Reset your password
							</span>
							<span className="label-regular colour-secondary">
								We'll send a reset link to your email
							</span>
						</div>
						<Input
							label={"Email"}
							labelType={"inside"}
							secondaryLabelGrey={null}
							info={false}
							value={resetEmail}
							onChange={(e) => setResetEmail(e.target.value)}
							placeholder={"youremail@email.com"}
							type="email"
							leftUnit={null}
							rightUnit={null}
							unitSwitch={false}
							leftUnitSelected={true}
							unitSwitchFunc={null}
							disabled={resetLoading}
							multiline={false}
							error={false}
							onMouseEnter={null}
							onMouseLeave={null}
							reset={false}
							resetFunc={null}
							resetPosition={null}
							focusMultiline={false}
							prompt={false}
						/>
						<Button
							buttonType="secondary-button"
							size="medium"
							text={resetLoading ? "Sending…" : "Send reset link"}
							cta={sendResetEmail}
							disabled={resetEmail === "" || resetLoading}
							leftIcon={null}
							leftIconClass=""
							rightIcon={null}
							rightIconClass=""
							alt=""
							standardIcon={false}
							badge={false}
							badgeVal={null}
							badgeBG={null}
							buttonRef={null}
							spinner={false}
							goldAccent={false}
						/>
					</div>
				)}

				{errorMessage !== "" && (
					<div className="auth-error">
						<span className="body-regular colour-error text-align-center">
							{errorMessage}
						</span>
					</div>
				)}

				{resetSent && (
					<div className="auth-reset-success">
						<span className="body-regular colour-primary">
							Check your inbox — reset link sent
						</span>
						<span
							className="label-regular colour-link cursor-pointer"
							onClick={() => { setResetSent(false); setShowForgotPassword(true); setErrorMessage(""); }}
						>
							Didn't get it? Try again
						</span>
					</div>
				)}

				<div className="auth-footer">
					<span className="body-regular colour-primary text-align-center">
						Don't have an account?{" "}
						<span
							onClick={() => navigate("/sign-up", { state: state })}
							className="body-regular-underline colour-link cursor-pointer"
						>
							Sign up here
						</span>
					</span>
				</div>

			</div>
		</div>
	);
};

export default Login;
